<template>
  <aside
    class="primary-rail"
    :class="{ collapsed: shell.sidebarCollapsed }"
    :style="{ width: `${railWidth}px` }"
  >
    <div class="rail-brand-zone">
      <img
        class="rail-brand"
        src="@/assets/figma/document-workbench/rail-brand.png"
        alt="璇玑"
        width="38"
        height="16"
        draggable="false"
      />
    </div>
    <nav class="rail-nav">
      <button
        type="button"
        class="rail-item"
        :class="{ active: isNewChat }"
        title="一诺"
        @click="onNewChat"
      >
        <span class="rail-icon rail-icon--yino" aria-hidden="true" />
        <span v-if="!shell.sidebarCollapsed" class="rail-label">一诺</span>
      </button>
      <button
        v-if="features.projectManagementEnabled"
        type="button"
        class="rail-item"
        :class="{ active: isProjects }"
        title="项目管理"
        @click="goProjects"
      >
        <span class="rail-icon rail-icon--project" aria-hidden="true" />
        <span v-if="!shell.sidebarCollapsed" class="rail-label">项目管理</span>
      </button>
      <button
        v-if="features.smartDocumentEnabled"
        type="button"
        class="rail-item"
        :class="{ active: isSmartDocument }"
        title="智能文档"
        @click="goSmartDocument"
      >
        <span class="rail-icon rail-icon--document" aria-hidden="true" />
        <span v-if="!shell.sidebarCollapsed" class="rail-label">智能文档</span>
      </button>
      <button
        v-if="features.aiToolsEnabled"
        type="button"
        class="rail-item"
        :class="{ active: isAiTools }"
        title="AI工具"
        @click="goAiTools"
      >
        <span class="rail-icon rail-icon--ai" aria-hidden="true" />
        <span v-if="!shell.sidebarCollapsed" class="rail-label">AI工具</span>
      </button>
      <button
        v-if="features.knowledgeEnabled"
        type="button"
        class="rail-item"
        :class="{ active: isKnowledge }"
        title="知识空间"
        @click="goKnowledge"
      >
        <span class="rail-icon rail-icon--knowledge" aria-hidden="true" />
        <span v-if="!shell.sidebarCollapsed" class="rail-label">知识空间</span>
      </button>
    </nav>
    <div class="rail-footer">
      <div class="rail-divider" aria-hidden="true" />
      <button
        type="button"
        class="rail-toggle"
        :title="shell.sidebarCollapsed ? '展开侧边栏' : '缩进侧边栏'"
        @click="shell.toggleSidebarCollapsed()"
      >
        <span class="rail-menu-icon" aria-hidden="true" />
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, ref, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppShellStore } from '@/stores/useAppShellStore';
import { useFeatureModulesStore } from '@/stores/useFeatureModulesStore';
import type AiAssistantPanel from '@/components/AiAssistantPanel.vue';

const route = useRoute();
const router = useRouter();
const shell = useAppShellStore();
const features = useFeatureModulesStore();
const aiPanelRef = inject<Ref<InstanceType<typeof AiAssistantPanel> | null>>('aipdAiPanelRef', ref(null));

const railWidth = computed(() => (shell.sidebarCollapsed ? shell.PRIMARY_RAIL_COLLAPSED_WIDTH : shell.PRIMARY_RAIL_WIDTH));

const isNewChat = computed(() => route.name === 'Dashboard' && shell.dashboardExpandedAiComposer);
const isProjects = computed(() =>
  !isNewChat.value
  && (route.name === 'Dashboard'
    || (String(route.name || '').startsWith('Project') && route.name !== 'SmartDocumentWorkbench')),
);
const isSmartDocument = computed(
  () => route.name === 'SmartDocumentWorkbench' || route.name === 'SmartDocumentWorkbenchHome',
);
const isAiTools = computed(() => route.name === 'AiTools');
const isKnowledge = computed(() => route.name === 'KnowledgeSpace');

async function onNewChat() {
  if (route.name !== 'Dashboard') {
    await router.push({ name: 'Dashboard' });
    await nextTick();
  }
  shell.setDashboardExpandedAiComposer(true);
  shell.requestNewAiSession();
}

async function goProjects() {
  shell.setDashboardExpandedAiComposer(false);
  if (route.name !== 'Dashboard') {
    await router.push({ name: 'Dashboard' });
  }
  await nextTick();
  const panel = aiPanelRef.value as { expandRightPanel?: (force?: boolean) => void } | null;
  panel?.expandRightPanel?.(true);
}

async function goSmartDocument() {
  // 侧栏「智能文档」始终进入个人工作台，与当前打开的项目解耦；
  // 项目内文档资料面板仍可通过 SmartDocumentWorkbench 进入项目工作区。
  shell.setDashboardExpandedAiComposer(false);
  await router.push({ name: 'SmartDocumentWorkbenchHome' });
}

function goAiTools() {
  shell.setDashboardExpandedAiComposer(false);
  void router.push({ name: 'AiTools' });
}

function goKnowledge() {
  shell.setDashboardExpandedAiComposer(false);
  void router.push({ name: 'KnowledgeSpace' });
}
</script>

<style scoped>
/*
 * UI 稿按顶栏 77→56 缩放 (k≈0.727)：
 * 展开 81 / 收起 55 · 品牌区 56（与 Topbar 底边共线）· item 65×70 · active 65 · icon 18 · label 12
 */
.primary-rail {
  --rail-primary: #0052d9;
  --rail-active-bg: #ecf2fe;
  --rail-ink: #000000;
  --rail-label: #757575;
  --rail-divider: #e7e7e7;
  --rail-radius: 10px;
  --rail-icon-size: 18px;
  --rail-item-w: 65px;
  --rail-item-slot: 70px;
  --rail-item-active-h: 65px;
  --rail-collapsed-item: 36px;
  --rail-collapsed-slot: 41px;

  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 81px;
  flex-shrink: 0;
  background: #fff;
  /* 顶栏 56px 内不画右边框，避免「璇玑」右侧出现竖线；56px 以下保留 */
  border-right: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  box-sizing: border-box;
  z-index: 1002;
  overflow: hidden;
  transition: width 0.2s ease;
}

/* 壳层灰底留白衔接：不再画满高右边框，白轨与灰底自然分界 */

.primary-rail.collapsed {
  width: 55px;
}

/* 55 内容 + 1 底部分割线 = 56，与 Topbar 底边对齐 */
.rail-brand-zone {
  flex-shrink: 0;
  width: 100%;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-bottom: 1px solid var(--rail-divider);
}

.rail-brand {
  width: 38px;
  height: 16px;
  display: block;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

.rail-divider {
  width: 100%;
  height: 1px;
  background: var(--rail-divider);
  flex-shrink: 0;
}

.rail-nav {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 26px;
  box-sizing: border-box;
}

.primary-rail.collapsed .rail-nav {
  padding-top: 28px;
}

.rail-item {
  width: var(--rail-item-w);
  height: var(--rail-item-slot);
  margin: 0;
  padding: 0;
  border: none;
  border-radius: var(--rail-radius);
  background: transparent;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--rail-ink);
  box-sizing: border-box;
  flex-shrink: 0;
}

.primary-rail.collapsed .rail-item {
  width: var(--rail-collapsed-slot);
  height: var(--rail-collapsed-slot);
  gap: 0;
}

.rail-item:hover {
  color: var(--rail-primary);
}

.rail-item.active {
  color: var(--rail-primary);
  background: var(--rail-active-bg);
  height: var(--rail-item-active-h);
  margin-top: 2.5px;
  margin-bottom: 2.5px;
}

.primary-rail.collapsed .rail-item.active {
  width: var(--rail-collapsed-item);
  height: var(--rail-collapsed-item);
  margin-top: 2.5px;
  margin-bottom: 2.5px;
}

.rail-item:not(.active) .rail-label {
  color: var(--rail-label);
}

.rail-item.active .rail-label,
.rail-item:hover .rail-label {
  color: inherit;
}

.rail-item:not(.active) .rail-icon {
  background: var(--rail-ink);
}

.rail-item.active .rail-icon,
.rail-item:hover .rail-icon {
  background: currentColor;
}

.rail-icon {
  width: var(--rail-icon-size);
  height: var(--rail-icon-size);
  display: block;
  flex-shrink: 0;
  background: var(--rail-ink);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}

.rail-icon--yino {
  -webkit-mask-image: url('../assets/figma/document-workbench/rail-yino.png');
  mask-image: url('../assets/figma/document-workbench/rail-yino.png');
}

.rail-icon--project {
  -webkit-mask-image: url('../assets/figma/document-workbench/rail-project.svg');
  mask-image: url('../assets/figma/document-workbench/rail-project.svg');
}

.rail-icon--document {
  -webkit-mask-image: url('../assets/figma/document-workbench/rail-document.svg');
  mask-image: url('../assets/figma/document-workbench/rail-document.svg');
}

.rail-icon--ai {
  -webkit-mask-image: url('../assets/figma/document-workbench/rail-ai.svg');
  mask-image: url('../assets/figma/document-workbench/rail-ai.svg');
}

.rail-icon--knowledge {
  -webkit-mask-image: url('../assets/figma/document-workbench/rail-knowledge.svg');
  mask-image: url('../assets/figma/document-workbench/rail-knowledge.svg');
}

.rail-label {
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
  text-align: center;
  white-space: nowrap;
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  letter-spacing: 0;
}

.rail-footer {
  margin-top: auto;
  width: 100%;
  height: 57px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.rail-footer .rail-divider {
  margin-bottom: 22px;
}

.rail-toggle {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--rail-radius);
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
}

.rail-toggle:hover {
  background: rgba(0, 82, 217, 0.06);
}

.rail-menu-icon {
  width: 14px;
  height: 11px;
  display: block;
  background:
    linear-gradient(var(--rail-ink), var(--rail-ink)) 0 0 / 100% 1.5px no-repeat,
    linear-gradient(var(--rail-ink), var(--rail-ink)) 0 50% / 100% 1.5px no-repeat,
    linear-gradient(var(--rail-ink), var(--rail-ink)) 0 100% / 100% 1.5px no-repeat;
}

.rail-toggle:hover .rail-menu-icon {
  background:
    linear-gradient(var(--rail-primary), var(--rail-primary)) 0 0 / 100% 1.5px no-repeat,
    linear-gradient(var(--rail-primary), var(--rail-primary)) 0 50% / 100% 1.5px no-repeat,
    linear-gradient(var(--rail-primary), var(--rail-primary)) 0 100% / 100% 1.5px no-repeat;
}
</style>
